import { Alert, Card, Empty, Skeleton, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useFetch } from "../../hooks/useFetch";
import { CotaPeriodoProximo } from "../../interfaces/PeriodosProximosDoisAnosResumo";
import { buscarPeriodosProximosDoisAnosService } from "../../services/buscarPeriodosProximosDoisAnosService";
import {
  Container,
  Description,
  Header,
  Title,
} from "../ViewDashboardOperacional/styles";

function getNomeEmpreendimento(cota: CotaPeriodoProximo) {
  return (
    cota.nomeEmpreendimento ??
    cota.AptoTable?.EmpreendimentoTable?.nome ??
    "-"
  );
}

function getNomeApartamento(cota: CotaPeriodoProximo) {
  return cota.nomeApartamento ?? cota.nomeApto ?? cota.AptoTable?.nome ?? "-";
}

export default function ViewDashboardOperacionalCotasSemPeriodos() {
  const {
    data: resumo,
    isLoading,
    error,
  } = useFetch(true, buscarPeriodosProximosDoisAnosService, []);

  const cotas = resumo?.cotasSemPeriodos ?? [];
  const empreendimentos = Array.from(
    new Set(cotas.map(getNomeEmpreendimento).filter((nome) => nome !== "-"))
  );

  const columns: ColumnsType<CotaPeriodoProximo> = [
    {
      title: "Nome do empreendimento",
      key: "nomeEmpreendimento",
      width: 300,
      filters: empreendimentos.sort((a, b) => a.localeCompare(b)).map((nome) => ({
        text: nome,
        value: nome,
      })),
      filterSearch: true,
      onFilter: (value, record) => getNomeEmpreendimento(record) === value,
      render: (_, record) => getNomeEmpreendimento(record),
    },
    {
      title: "Nome do apartamento",
      key: "nomeApartamento",
      width: 260,
      render: (_, record) => getNomeApartamento(record),
    },
    {
      title: "Número da cota",
      dataIndex: "numeroCota",
      key: "numeroCota",
      width: 160,
    },
  ];

  return (
    <Container>
      <Header>
        <Title>Cotas de cronograma fixo sem períodos</Title>
        <Description>
          Cotas ativas de empreendimentos com cronograma fixo que não possuem
          período ativo com check-in nos próximos dois anos.
        </Description>
      </Header>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar as cotas sem períodos."
          description="Verifique a conexão com a API e tente novamente."
        />
      ) : !resumo ? (
        <Alert
          type="warning"
          showIcon
          message="Nenhuma resposta foi retornada para a lista de cotas."
        />
      ) : cotas.length === 0 ? (
        <Card>
          <Empty description="Nenhuma cota sem períodos encontrada." />
        </Card>
      ) : (
        <Card
          title={`Total de cotas sem períodos: ${cotas.length}`}
          bodyStyle={{ padding: 0 }}
        >
          <Table<CotaPeriodoProximo>
            rowKey={(record, index) =>
              String(record.cotaId ?? record.id ?? `${record.numeroCota}-${index}`)
            }
            columns={columns}
            dataSource={cotas}
            scroll={{ x: 720 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
          />
        </Card>
      )}
    </Container>
  );
}
